import React from 'react'
import { Box, ButtonBase, Grid, IconButton, Popover, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IconSeeker, SelectSeeker } from '@ui-seeker/core'
import { AppAntSwitch, AppBasicTable, AppButtonIcon, AppCardTitle, AppRetailerBlock, AppToggleButton } from '@components'
import AnalyzersLegendToolbar from 'Pages/AnalyzersV2/@components/Table/Toolbar/Legend'
import { useCellColors } from 'Pages/AnalyzersV2/@components/Table/columns/Data/@hooks'
import { dataColors, dsColorNew } from '@palette'
import AnalyzerDemoChart from './DemoChart'

// The analyzer page as the Virtual Tour demo draws it when there is no report loaded: price table
// (toolbar, cells, legend footer), cell popover, prices chart and, for Brokers / Direct Site, the
// "Price Table sorted by Categories". It carries the same data-tour-id targets as the real page.

const POPOVER_LINKS = [
	{ icon: 'chart-line', label: 'Evolution', title: 'Evolution Chart', tourId: 'tour-popover-evolution' },
	{ icon: 'magnifying-glass', label: 'Real time', title: 'Real-Time Searches', tourId: 'tour-popover-realtime' },
	{ icon: 'image', label: 'Screenshot', title: 'Screenshot', tourId: 'tour-popover-screenshot' },
]

const SITE_COLORS = [dataColors.emerald500, dataColors.violet500, dataColors.sky500, dataColors.pink500, dsColorNew.appBlue]

// The column label as the real header draws it (Table/columns/Data/Header): a date shows its weekday on top
const DemoColumnHeader = ({ label, day }) => <Box sx={{ my: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
	<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3, py: 0.5, px: 1, borderRadius: '6px' }}>
		{day && <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{day}</Typography>}
		<Typography sx={day
			? { fontSize: 11, fontWeight: 400, lineHeight: 1.3, opacity: 0.8 }
			: { fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{label}</Typography>
	</Box>
</Box>

// The real price cell (Table/columns/Data): bordered box with the value, its change and the previous value;
// ratings carry the star the real cell draws when there is no currency
function DemoPriceCell({ value, isCheapest, isMostExpensive, isRating }) {
	const { bgColor, borderColor } = useCellColors(isCheapest, isMostExpensive)
	const text = isRating ? value.toFixed(1) : value.toFixed(2)
	const Value = ({ color }) => <Typography variant="subtitle1" sx={{ color, display: 'flex', alignItems: 'center', gap: 0.5 }}>
		{text}
		{isRating && <IconSeeker icon="star" sx={{ color: dsColorNew.noteAmber }}/>}
	</Typography>

	return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
		<Box sx={{
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			width: 130, minHeight: 77, border: '1px solid', borderColor, borderRadius: '8px', backgroundColor: bgColor,
		}}>
			<Box sx={{ width: 110, m: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', justifyItems: 'center' }}>
				<Value color={dsColorNew.textDark}/>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<IconSeeker icon="equals" sx={{ mt: 0.2 }}/>
					<Typography variant="body2">0%</Typography>
				</Box>
				<Value color={dsColorNew.textMuted}/>
			</Box>
		</Box>
	</Box>
}

// Header and zebra of the real analyzer tables (AnalyzersTable / CategoriesTable)
const headerCellProps = (_, idx) => ({
	sx: {
		p: 0.5,
		backgroundColor: dsColorNew.tableHead,
		...!idx && { position: 'sticky', left: 0, zIndex: 3, textAlign: 'left !important', pl: 1.75 },
	}
})

const bodyCellProps = (_, row, __, colIdx) => ({
	sx: {
		p: colIdx === 0 ? 0 : '7px 6px',
		backgroundColor: row.rowIdx % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt,
		...colIdx === 0 && { position: 'sticky', left: 0, pl: 1.75, borderRight: `1px solid ${dsColorNew.grayBorder}` },
	},
	...row.rowIdx === 0 && colIdx === 1 && { 'data-tour-id': 'tour-price-cell' },
})

// The Cars selector's row of DOR / LOR (CU-869fmpqfm), so the tour step has something to point at
function DemoCarPickRow() {
	return <Box data-tour-id="tour-car-pick" sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
		<TextField size="small" label="Cars" value="All cars" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} sx={{ width: 300 }}/>
		<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>Minimum across 6 categories — pick a car to compare like with like.</Typography>
	</Box>
}

function DemoTableToolbar({ isRating, withCarPick = false }) {
	return <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, width: '100%', mb: 1.5 }}>
		<AppCardTitle icon="table-cells" title="Analyzer: Price Table"/>
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2, ml: 'auto' }}>
			<AppAntSwitch falseText="Show details" trueText="Hide details" checked={false} onChange={() => {}}/>
			{!isRating && <Box data-tour-id="tour-features-selector" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<SelectSeeker value={[]} onChange={() => {}} multiple sx={{ width: 220 }} label="Features" placeholder="Any feature" options={[]}/>
			</Box>}
			{!isRating && <Box data-tour-id="tour-minmax-toggle" sx={{ display: 'flex', alignItems: 'center' }}>
				<AppToggleButton value="min" onChange={() => {}} tabs={[{ value: 'min', label: 'Min' }, { value: 'max', label: 'Max' }]}/>
			</Box>}
		</Box>
		{withCarPick && <DemoCarPickRow/>}
	</Box>
}

// "Price Table sorted by Categories": per column, every price from cheapest to most expensive with its site
// under it, tinted with that site's colour
function DemoCategoriesTable({ columns, rows }) {
	const colours = Object.fromEntries(rows.map((r, i) => [r.name, SITE_COLORS[i % SITE_COLORS.length]]))
	const sorted = columns.map((_, i) => rows.map(r => ({ name: r.name, price: r.prices[i] })).sort((a, b) => a.price - b.price))
	const data = rows.map((_, rowIdx) => ({ rowIdx, ...Object.fromEntries(columns.map((c, i) => [c.label, sorted[i][rowIdx]])) }))

	return <AppBasicTable
		cardProps={{ 'data-tour-id': 'tour-categories-table' }}
		Components={{
			Title: () => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1.5 }}>
				<AppCardTitle icon="layer-group" title="Price Table sorted by Categories"/>
				<AppButtonIcon icon="expand-wide" tooltip="Fullscreen" iconWeight="solid"/>
			</Box>
		}}
		headers={[columns.map(c => ({
			render: () => <Box sx={{ my: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, py: 0.5, px: 1 }}>
				<IconSeeker icon="magnifying-glass" sx={{ fontSize: 11, opacity: 0.8 }}/>
				<Typography sx={{ fontSize: 12, fontWeight: 600 }}>{c.label}</Typography>
			</Box>
		}))]}
		setHeaderCellProps={() => ({ sx: { p: 0.5, backgroundColor: dsColorNew.tableHead } })}
		setBodyCellProps={(_, row) => ({
			sx: { p: '6px 4px', backgroundColor: row.rowIdx % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt },
		})}
		data={data}
		columns={columns.map(c => ({
			name: c.label,
			render: ({ name, price }) => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Box sx={{
					display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
					width: 140, height: 100, p: 1, borderRadius: '8px', border: '1px solid',
					borderColor: alpha(colours[name], 0.6), backgroundColor: alpha(colours[name], 0.1),
				}}>
					<Typography variant="subtitle1">{price.toFixed(2)} EUR</Typography>
					<AppRetailerBlock retailer={name}/>
				</Box>
			</Box>,
		}))}
	/>
}

// The cell popover as the real AnalyzerPopover draws it: title, features and links
function DemoCellPopover({ demoPopoverOpen, pickup }) {
	const anchorEl = demoPopoverOpen.value ? document.querySelector('[data-tour-id="tour-price-cell"]') : null
	const features = [
		{ label: 'Category', value: 'Economy' },
		{ label: 'Fuel', value: 'Full to Full' },
		{ label: 'Payment', value: 'Prepaid' },
		{ label: 'PickUp', value: pickup },
	]

	return <Popover
		open={!!anchorEl}
		anchorEl={anchorEl}
		onClose={() => {}}
		anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
		transformOrigin={{ vertical: 'center', horizontal: 'left' }}
		disableScrollLock
		PaperProps={{ 'data-tour-id': 'tour-demo-popover', sx: {
			p: 0, width: 300, borderRadius: '12px', border: `1px solid ${dsColorNew.grayBorder}`,
			boxShadow: `0 16px 40px ${alpha(dsColorNew.navy, 0.16)}`, backgroundColor: dsColorNew.white,
		} }}
	>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.5, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
			<Box sx={{
				width: 30, height: 30, borderRadius: '8px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
				backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue,
			}}>
				<IconSeeker icon="car" sx={{ fontSize: 13 }}/>
			</Box>
			<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle, minWidth: 0 }}>Volkswagen Golf 1.6 TDI</Typography>
			<IconButton
				data-tour-id="tour-popover-close"
				aria-label="Close"
				onClick={() => { demoPopoverOpen.value = false }}
				sx={{ ml: 'auto', width: 28, height: 28, borderRadius: '8px', color: dsColorNew.textSubtle }}
			>
				<IconSeeker icon="xmark" sx={{ fontSize: 13 }}/>
			</IconButton>
		</Box>
		<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px', px: 1.75, py: 1.5 }}>
			{features.map(({ label, value }) => <Box key={label} sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
				<Typography sx={{ fontSize: 13, fontWeight: 500, color: dsColorNew.textTitle }}>{value}</Typography>
			</Box>)}
		</Box>
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1.75, py: 1.25, borderTop: `1px solid ${dsColorNew.grayBorder}`, backgroundColor: dsColorNew.tableRowAlt, borderRadius: '0 0 12px 12px' }}>
			{POPOVER_LINKS.map(({ icon, label, title, tourId }) => <Box key={icon} data-tour-id={tourId}>
				<ButtonBase title={title} sx={{
					gap: 0.75, height: 28, px: 1.1, borderRadius: '7px', fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'default',
					color: dsColorNew.appBlue, backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
				}}>
					<IconSeeker icon={icon} sx={{ fontSize: 12 }}/>
					{label}
				</ButtonBase>
			</Box>)}
		</Box>
	</Popover>
}

/**
 * columns: [{ label, day? }]  — the table's columns (LORs, dates, categories…)
 * rows:    [{ name, country?, prices: [] }] — one value per column
 * chart:   { title, type: 'line' | 'column', extraAction? }
 */
export default function AnalyzerDemoData({
	demoPopoverOpen, firstColumn = 'Website', columns, rows, chart, pickup = '05 Jan 2025',
	isRating = false, withCategories = false, withCarPick = false,
}) {
	const mins = columns.map((_, i) => Math.min(...rows.map(r => r.prices[i])))
	const maxs = columns.map((_, i) => Math.max(...rows.map(r => r.prices[i])))
	const tableData = rows.map((r, rowIdx) => ({
		rowIdx, name: r.name, country: r.country,
		...Object.fromEntries(columns.map((c, i) => [c.label, r.prices[i]])),
	}))

	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<AppBasicTable
				cardProps={{ 'data-tour-id': 'tour-price-table' }}
				Components={{ Title: () => <DemoTableToolbar isRating={isRating} withCarPick={withCarPick}/> }}
				headers={[[
					{ label: firstColumn },
					...columns.map((c, i) => ({
						render: () => <DemoColumnHeader {...c}/>,
						...i === 0 && { 'data-tour-id': 'tour-column-header' },
					})),
				]]}
				setHeaderCellProps={headerCellProps}
				setBodyCellProps={bodyCellProps}
				onDownload={() => {}}
				footer={<AnalyzersLegendToolbar isRating={isRating}/>}
				data={tableData}
				columns={[
					{ name: 'name', render: (name, { country }) => <Box sx={{ display: 'flex', alignItems: 'center', width: 150 }}><AppRetailerBlock retailer={name} country={country}/></Box> },
					...columns.map((c, i) => ({
						name: c.label,
						render: v => <DemoPriceCell value={v} isRating={isRating} isCheapest={v === mins[i]} isMostExpensive={v === maxs[i]}/>,
					})),
				]}
			/>
		</Grid>

		<Grid item xs={12}>
			<AnalyzerDemoChart
				type={chart.type}
				title={chart.title}
				extraAction={chart.extraAction}
				decimals={isRating ? 1 : 2}
				xLabels={columns.map(c => c.label)}
				series={rows.map(r => ({ name: r.name, values: r.prices }))}
			/>
		</Grid>

		{withCategories && <Grid item xs={12}>
			<DemoCategoriesTable columns={columns} rows={rows}/>
		</Grid>}

		{!isRating && <DemoCellPopover demoPopoverOpen={demoPopoverOpen} pickup={pickup}/>}
	</Grid>
}
